import Vue from "vue";

/**
 * Serviço global de confirmação/feedback.
 *
 * Uso (em qualquer componente Options API):
 *   const ok = await this.$confirm({ type: "delete", title: "...", message: "..." });
 *   if (ok) { ... }
 *
 * Em setup() / composables:
 *   import { confirm } from "@/services/confirmService";
 *   const ok = await confirm({ ... });
 *
 * Tipos: "confirm" | "delete" | "warning" | "success" | "error" | "info".
 * Os tipos de feedback (success/error/info) abrem como alerta (só botão OK) e
 * resolvem `true` ao fechar.
 */

// Presets visuais por tipo. `variant` mapeia para a classe de gradiente do header.
const PRESETS = {
  confirm: {
    variant: "primary",
    title: "Confirmar",
    headerIcon: "mdi-help-circle-outline",
    bodyIcon: "mdi-help-circle-outline",
    confirmText: "Confirmar",
    confirmColor: "primary",
    alertOnly: false,
  },
  delete: {
    variant: "danger",
    title: "Confirmar exclusão",
    headerIcon: "mdi-delete-alert",
    bodyIcon: "mdi-trash-can-outline",
    confirmText: "Excluir",
    confirmColor: "error",
    alertOnly: false,
    // salvia-776: o motivo passa a ser exigido AQUI, no preset — e por isso
    // que os 34 pontos de exclusao do sistema ganham o campo sem que nenhum
    // deles precise mudar uma linha.
    //
    // Quem NAO quiser o campo passa `exigeMotivo: false` explicitamente. Hoje
    // so o "Limpar notificacoes" faz isso: e descarte de caixa de avisos ja
    // lidos, nao exclusao de registro. Pedir justificativa ali ensinaria o
    // usuario a digitar qualquer coisa por reflexo -- e esse reflexo
    // contaminaria os outros 34, onde o motivo importa.
    exigeMotivo: true,
  },
  warning: {
    variant: "warning",
    title: "Atenção",
    headerIcon: "mdi-alert",
    bodyIcon: "mdi-alert-outline",
    confirmText: "Continuar",
    confirmColor: "warning",
    alertOnly: false,
  },
  success: {
    variant: "success",
    title: "Sucesso!",
    headerIcon: "mdi-check-circle",
    bodyIcon: "mdi-check-circle-outline",
    confirmText: "OK",
    confirmColor: "success",
    alertOnly: true,
  },
  error: {
    variant: "danger",
    title: "Erro",
    headerIcon: "mdi-alert-circle",
    bodyIcon: "mdi-alert-circle-outline",
    confirmText: "OK",
    confirmColor: "error",
    alertOnly: true,
  },
  info: {
    variant: "primary",
    title: "Informação",
    headerIcon: "mdi-information",
    bodyIcon: "mdi-information-outline",
    confirmText: "OK",
    confirmColor: "primary",
    alertOnly: true,
  },
};

/**
 * Motivos de exclusao — salvia-776, reuniao de 06/08.
 *
 * Lista fechada + "Outro" com texto livre: a lista da relatorio e evita o
 * campo virar despejo de "x"; o texto livre existe porque nenhuma lista
 * cobre tudo.
 *
 * "A pedido do paciente" fica SEPARADO de "cadastrado por engano" de
 * proposito: tem peso juridico. Num pedido de exclusao por LGPD, e este
 * registro que prova que houve pedido, e quando.
 */
export const MOTIVOS_DE_EXCLUSAO = [
  "Cadastrado por engano",
  "Duplicado",
  "Informação incorreta",
  "A pedido do paciente",
  "Não se aplica mais",
  "Outro",
];

/** O valor que abre a caixa de texto. */
export const MOTIVO_OUTRO = "Outro";

/**
 * Minimo do texto livre. So vale para "Outro" -- escolher uma opcao da lista
 * ja E o motivo e nao pede texto nenhum.
 *
 * Dez caracteres barram "x", "." e "erro" (que, alias, ja esta na lista como
 * "Informação incorreta"). Quem marca "Outro" tem algo que a lista nao cobre,
 * entao tem o que escrever. Exigir menos deixa a regra passar por reflexo;
 * exigir muito mais vira castigo e produz texto inventado.
 */
export const MOTIVO_MINIMO = 10;

/**
 * O motivo da ultima exclusao confirmada, esperando para ser enviado.
 *
 * Existe para o interceptor do axios pegar sozinho (ver services/http.js) e
 * mandar no cabecalho de TODO DELETE. A alternativa seria reescrever as 34
 * chamadas `http.delete(url)` para `http.delete(url, { data })`, ja que o
 * axios nao manda corpo em DELETE.
 *
 * CONSUMO UNICO: quem le, zera. O motivo pertence AQUELA exclusao -- deixar
 * sobrando faria a proxima requisicao carregar a justificativa da anterior.
 */
let motivoPendente = null;

export function consumirMotivoDeExclusao() {
  const motivo = motivoPendente;
  motivoPendente = null;
  return motivo;
}

// Estado reativo compartilhado (singleton renderizado em App.vue).
const state = Vue.observable({
  visible: false,
  opts: {},
  _resolve: null,
});

/**
 * Abre o diálogo e devolve uma Promise<boolean>:
 *   - resolve(true)  → confirmado / OK
 *   - resolve(false) → cancelado / fechado
 */
export function confirm(options = {}) {
  const type = options.type || "confirm";
  const preset = PRESETS[type] || PRESETS.confirm;

  state.opts = {
    type,
    ...preset,
    ...options, // overrides do chamador (title, message, confirmText, etc.)
  };
  state.visible = true;

  return new Promise((resolve) => {
    state._resolve = resolve;
  });
}

function settle(result) {
  state.visible = false;
  const resolve = state._resolve;
  state._resolve = null;
  if (resolve) resolve(result);
}

/**
 * Confirmacao de exclusao COM motivo.
 *
 * Guarda o motivo para o interceptor e resolve `true`, nao o texto: os 34
 * chamadores fazem `if (!ok) return;` e continuam funcionando sem alteracao.
 */
export function acceptDelete(motivo) {
  motivoPendente = motivo;
  settle(true);
}

// Ações usadas pelo componente renderer.
export const confirmState = state;
export function acceptConfirm() {
  settle(true);
}
export function cancelConfirm() {
  settle(false);
}
